import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import KitForm from '@/components/brand/KitForm';
import TemplatesTab from '@/components/brand/TemplatesTab';
import Generator from '@/components/brand/Generator';
import VideoTab from '@/components/brand/VideoTab';
import { fetchBrandKit, fetchVisibleTemplates, saveBrandKit, templateEntries, type BrandKit, type BrandKitOut } from '@/lib/brand-kit';
import { qk } from '@/lib/queries';
import { useCan, useTasksOnly } from '@/hooks/use-scopes';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/cn';

type Tab = 'kit' | 'templates' | 'generate' | 'video';
const TABS: { key: Tab; label: string }[] = [
  { key: 'kit', label: 'Brand kit' }, { key: 'templates', label: 'Templates' }, { key: 'generate', label: 'Generate' },
  { key: 'video', label: 'Video' },
];
const errText = (e: unknown) => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x: { msg?: string }) => x.msg).join('; ');
  return typeof d === 'string' ? d : 'Could not save';
};

/** A clinic's brand kit, its templates, and the static generator (Aziz 2026-10-21).
 *  Who changes what (Aziz 2026-10-22): the identity is managers only (clients.write).
 *  Templates (owner 2026-10-24): the Templates tab saves each design on its own: base templates by
 *  Brand details, everyone else's as their own copies (TemplatesTab). This page's Save is the kit.
 *  The clinic's main portal login opens it too (Aziz 2026-10-25), on its own clinic: the kit read
 *  only, its own designs under the brand rules, and the generator. */
export default function BrandKitPage({ portal = false }: { portal?: boolean }) {
  const { user } = useAuth();
  const params = useParams();
  const id = (portal ? user?.client_id : params.id) ?? '';
  const qc = useQueryClient();
  // Brand details (Aziz 2026-10-22): granted per person as brand.write; clients.write keeps it
  const canBrand = useCan('brand.write');
  const canClients = useCan('clients.write');
  const canEdit = canBrand || canClients;
  // tasks-level people have no clinic page: they came from Brand kits
  const tasksOnly = useTasksOnly() !== false;
  const q = useQuery({ queryKey: ['brand-kit', id], queryFn: () => fetchBrandKit(id), enabled: !!id });
  const vis = useQuery({ queryKey: qk.brandTemplates(id), queryFn: () => fetchVisibleTemplates(id) });
  const [kit, setKit] = useState<BrandKit | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<Tab>(() => (sessionStorage.getItem('brand-kit.tab') as Tab) || 'kit');
  useEffect(() => { try { sessionStorage.setItem('brand-kit.tab', tab); } catch { /* fine */ } }, [tab]);
  useEffect(() => { if (q.data && !dirty) setKit(q.data.kit); }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (q.isLoading || !kit) return <p className="text-sm text-muted">Loading the brand kit...</p>;
  if (q.isError) return <p className="text-sm text-red-400">Could not load the brand kit.</p>;
  const data = q.data as BrandKitOut;

  const set = (fn: (k: BrandKit) => void) => {
    setKit((k) => { const next = structuredClone(k!); fn(next); return next; });
    setDirty(true);
  };
  // the base templates are saved by the Templates tab; the kit save carries the latest of them
  const save = async () => {
    setSaving(true);
    try {
      const latest = (qc.getQueryData(['brand-kit', id]) as BrandKitOut | undefined)?.kit.templates ?? kit.templates;
      const out = await saveBrandKit(id, { ...kit, templates: latest });
      qc.setQueryData(['brand-kit', id], out);
      setKit(out.kit); setDirty(false);
      toast.success('Brand kit saved');
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {!portal && (
        <Link to={tasksOnly ? '/brand-kits' : `/clients/${id}`} className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent-400 mb-3">
          <ArrowLeft size={12} /> Back to {tasksOnly ? 'Brand kits' : data.client_name}
        </Link>
      )}
      <PageHeader
        title={`${data.client_name}: brand kit`}
        subtitle={portal
          ? 'Make your own designs from the templates and generate statics in your fonts, colours and rules. KAF PRO looks after the brand details.'
          : 'Fed once by the clinic. Every static is generated from it, in its own fonts, colours and rules.'}
        actions={canEdit && (tab === 'kit' || dirty) ? (
          <Button icon={<Save size={14} />} loading={saving} disabled={!dirty} onClick={save} data-testid="kit-save">
            {dirty ? 'Save changes' : 'Saved'}
          </Button>
        ) : undefined}
      />

      <Card className="mb-4" data-testid="kit-missing">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="text-3xl font-semibold text-white">{data.complete_pct}%</div>
            <div className="text-sm text-muted">complete{data.missing.length ? `, ${data.missing.length} things still needed from ${portal ? 'you' : 'the clinic'}` : ''}</div>
          </div>
          <div className="h-2 flex-1 min-w-[160px] max-w-md rounded bg-slate-800 overflow-hidden">
            <div className="h-full bg-accent-400" style={{ width: `${data.complete_pct}%` }} />
          </div>
        </div>
        {data.missing.length > 0 ? (
          <details className="mt-3">
            <summary className="text-sm text-accent-400 cursor-pointer">{portal ? 'What we still need from you' : 'What to ask the clinic for'}</summary>
            <ul className="mt-2 grid md:grid-cols-2 gap-x-6 gap-y-1">
              {data.missing.map((m) => (
                <li key={m.area + m.item} className="text-sm text-muted-strong"><span className="text-muted">{m.area}:</span> {m.item} <span className="text-xs text-muted">({m.needed_for})</span></li>
              ))}
            </ul>
          </details>
        ) : <p className="mt-2 text-sm text-emerald-400 flex items-center gap-1"><CheckCircle2 size={14} /> Everything is in. New statics are plug and play.</p>}
      </Card>

      <div className="flex gap-1 mb-4 border-b border-slate-800">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={cn('px-4 h-10 text-sm -mb-px border-b-2', tab === t.key ? 'border-accent-400 text-white' : 'border-transparent text-muted hover:text-white')}>
            {t.label}
          </button>
        ))}
        {dirty && <span className="ml-auto self-center text-xs text-amber-300">Unsaved changes</span>}
      </div>

      {tab === 'kit' && <KitForm clientId={id} kit={kit} people={data.people} set={set} readOnly={!canEdit} />}
      {(tab === 'templates' || tab === 'generate') && !vis.data && (vis.isError
        ? <p className="text-sm text-red-400">Could not load the templates.</p> : <p className="text-sm text-muted">Loading the templates...</p>)}
      {tab === 'templates' && vis.data && (
        <TemplatesTab clientId={id} kit={kit} people={data.people} visible={vis.data}
          onBase={(out) => { qc.setQueryData(['brand-kit', id], out); setKit((k) => (k ? { ...k, templates: out.kit.templates } : k)); }} />
      )}
      {tab === 'generate' && vis.data && <Generator clientId={id} kit={kit} people={data.people} canManage={canEdit} templates={templateEntries(vis.data)}
        canSave={!portal || !!user?.can_upload_material} />}
      {tab === 'video' && <VideoTab clientId={id} />}
    </div>
  );
}
